/**
 * AsyncToolbarButton Component
 *
 * A wrapper around Fluent UI ToolbarButton that handles async operation loading states.
 * Shows a spinner and loading text while an operation is in progress.
 *
 * Usage:
 * <AsyncToolbarButton
 *   label="Delete"
 *   loadingLabel="Deleting..."
 *   icon={<Delete24Regular />}
 *   loading={isDeleting}
 *   onClick={handleDelete}
 *   disabled={selectedItems.size === 0}
 * />
 */

import { forwardRef } from 'react';
import { ToolbarButton, Spinner } from '@fluentui/react-components';
import type { ToolbarButtonProps } from '@fluentui/react-components';

export interface AsyncToolbarButtonProps extends Omit<ToolbarButtonProps, 'children'> {
  /** Text to display on the button */
  label: string;
  /** Text to display while loading (replaces label) */
  loadingLabel?: string;
  /** When true, shows loading spinner and prevents interactions */
  loading?: boolean;
}

export const AsyncToolbarButton = forwardRef<HTMLButtonElement, AsyncToolbarButtonProps>(
  function AsyncToolbarButton(props, ref) {
    const {
      label,
      loadingLabel,
      loading = false,
      icon,
      onClick,
      disabled,
      ...rest
    } = props;

    const displayIcon = loading ? <Spinner size="tiny" /> : icon;
    const displayLabel = loading && loadingLabel ? loadingLabel : label;

    return (
      <ToolbarButton
        {...rest}
        ref={ref}
        icon={displayIcon}
        onClick={loading ? undefined : onClick}
        disabled={disabled}
        disabledFocusable={loading && !disabled}
        aria-label={displayLabel}
      >
        {displayLabel}
      </ToolbarButton>
    );
  }
);
